import { Component, OnInit, Inject } from "@angular/core";
import { Observable, of } from "rxjs";
import { Router, ActivatedRoute, ParamMap } from "@angular/router";
import {
  MatDialog as MatDialog,
  MatDialogRef as MatDialogRef,
  MatDialogConfig as MatDialogConfig,
  MAT_DIALOG_DATA as MAT_DIALOG_DATA,
} from "@angular/material/dialog";
import { AlertifyService } from "../../_models/alertify.service";

import { TemplateService } from "../service/template.service";
import {
  AppSect,
  TemplateContent,
  ApplicabilityReviewDialogData,
} from "../model/TemplateRulebook";
import { diffString, decodeHtml, stripHtml } from "../shared/text-diff.util";

@Component({
  selector: "app-edit-applicability",
  templateUrl: "./edit-applicability.component.html",
  styleUrls: ["./edit-applicability.component.css"],
  standalone: false,
})
export class EditApplicabilityComponent implements OnInit {
  csskey: string;
  auditName: string;
  appkey: string;
  appSect$: Observable<AppSect[]>;
  selectedRbName: string;
  selectedAppkey: number;
  rulebookList: TemplateContent[];
  currentTopic: string;
  isAnswering: boolean;
  sharePermission: boolean; //

  // Tracks in-flight updateAppAnswer() saves so onBackSummary() can wait for them to land
  // before navigating - otherwise the summary page's getContent() can race ahead of the
  // last answer's save (which is what clears 'Needs Review' server-side) and read stale data.
  private pendingSaves: Promise<void>[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alertService: AlertifyService,
    private dialog: MatDialog,
    public service: TemplateService
  ) {}

  ngOnInit() {
    this.selectedRbName =
      this.service.decodeHtml(this.service.getRbName().value ?? "") ?? "";
    this.isAnswering = false;
    this.service.getCurrent().subscribe((res) => {
      //let item = res.findIndex(x => x.acronym == "Custom"); // Do not display custom question on applicablity page.

      this.rulebookList = res;
    });

    this.auditName = this.service.getAuditName();

    this.route.paramMap.subscribe(
      (paraMap) => (
        (this.csskey = paraMap.get("id")),
        (this.appkey = paraMap.get("appkey")),
        (this.sharePermission = this.service.isTemplateEditable(+this.csskey))
      )
    );
    this.currentTopic = this.service.getTopic(parseInt(this.appkey));
    this.loadApplicability(this.csskey, this.appkey);
  }

  loadApplicability(csskey: string, appkey: string) {
    try {
      this.service.showLoadingAnimation(true);
      this.appSect$ = new Observable<AppSect[]>();
      this.appSect$ = this.service.getApplicability(csskey, appkey);
      let re = /<b>Question:<\/b>/gi;

      this.appSect$.subscribe(
        (res) => {
          this.service.showLoadingAnimation(false);
          res.forEach((element) => {
            element.question = this.service
              .decodeHtml(element.question)
              .replace(re, ""); //decode html entities; remove <b>Question:</b> tag.
            element.exemption = this.service.decodeHtml(element.exemption);
          });
          // Only clear 'Needs Review' if every significant-change flag on this
          // rulebook's questions/exemptions has already been answered - merely
          // opening the page should not clear it.
          if (res.every((a) => !a.sigChangeUpdate)) {
            this.service.revokeReviewFlag(+csskey, +appkey, 1);
          }
        },
        (err) => {
          console.error("got an error:" + err);
          this.service.showLoadingAnimation(false);
          this.alertService.error(err.message);
        },
        () => this.service.showLoadingAnimation(false)
      );
    } catch (Error) {
      this.alertService.error(Error.message);
    }
  }

  onClickRulebook(appkey: number) {
    //console.log("received click event from child: " + appkey);
    this.selectedRbName = this.service.getRbName().value;
    this.appkey = this.service.getKey().value.toString();
    this.currentTopic = this.service.getTopic(parseInt(this.appkey));

    this.loadApplicability(this.csskey, appkey.toString());
  }

  onBackSummary() {
    // Wait for every in-flight answer save first - each one's success handler re-caches
    // this component's own (possibly stale, e.g. needsReview) rulebookList snapshot, so
    // clearing the cache has to happen after all of those, not before, or it just gets
    // immediately overwritten again.
    Promise.all(this.pendingSaves).then(() => {
      this.service.removeTemplateLocalStorage(+this.csskey);
      var myurl = "audithub/apptool/edit/" + this.csskey;
      this.router.navigateByUrl(myurl).then((err) => {
        console.log(err);
      });
    });
  }

  /**
   * Get the mirrored appkey of the rulebook.
   * @param appkey: the appkey of the rulebook.
   * @return: the mirrored appkey if found, otherwise return -1.
   */
  getMirroredAppkey(appkey: number): number {
    if (this.service.isDualLanguageTemplate() === false) return -1;
    for (const acronym of this.rulebookList) {
      for (const topic of acronym.topics) {
        const rb = topic.rulebooks.find((rb) => rb.appKey === appkey);
        if (rb) {
          const mirroredAcronym = this.service.getDualLanguageAcronym(
            acronym.acronym
          );
          if (!mirroredAcronym) return -1;
          const mirroredPub = this.rulebookList.find(
            (a) =>
              a.acronym === mirroredAcronym &&
              a.releaseNum === acronym.releaseNum
          );
          if (!mirroredPub) return -1;
          const mirroredTopic = mirroredPub.topics.find(
            (t) => t.topic === topic.topic
          );
          if (!mirroredTopic || !mirroredTopic.rulebooks.length) return -1;
          return mirroredTopic.rulebooks[0].appKey;
        }
      }
    }
    return -1;
  }

  /**
   * Answer the applicability questions.
   * @param answer
   */
  onAnswerApp(answer: string) {
    this.updateAppAnser(
      this.csskey,
      this.appkey ? parseInt(this.appkey) : 0,
      answer
    );
    let mirroredAppkey = this.getMirroredAppkey(
      this.appkey ? parseInt(this.appkey) : 0
    );
    if (mirroredAppkey !== -1) {
      this.updateAppAnser(this.csskey, mirroredAppkey, answer);
    }
  }

  updateAppAnser(csskey: string, appkey: number, answer: string) {
    this.service.showLoadingAnimation(true);
    this.isAnswering = true;
    const save = new Promise<void>((resolve) => {
      this.service.updateAppAnswer(csskey, appkey, answer).subscribe(
        (val) => {
          this.isAnswering = false;
          this.selectedRbName = this.selectedRbName + "";
          const rb = this.rulebookList
            .flatMap((arcon) => arcon.topics)
            .flatMap((tp) => tp.rulebooks)
            .find((rb) => rb.appKey == val.appkey);

          if (rb) {
            rb.percentage = this.service.calRulebookPercentage(answer);
            rb.checkNa = this.service.calCheckNa(answer);
            rb.appAnswer = answer;
          }
          // Update the cache
          this.service.cacheRulebookLocalStorage(
            parseInt(this.csskey),
            this.rulebookList
          );
          resolve();
        },
        (error) => {
          console.error("updateAppAnser() return: " + error.error);
          this.isAnswering = false;
          resolve();
        },
        () => {
          this.service.showLoadingAnimation(false);
        }
      );
    });
    this.pendingSaves.push(save);
  }

  /**
   * Called whenever an applicability question/exemption is answered. Once none of the
   * rulebook's questions are still flagged with a significant change, patch its 'Needs
   * Review' status in the cache immediately. This is only a best-effort local patch -
   * updateAppAnser's own async re-cache of its in-memory rulebookList (needsReview never
   * updated there) can still overwrite it, which is why onBackSummary() drops the whole
   * cache after all saves land, forcing a real re-fetch instead of relying on this alone.
   */
  onSigResolved(resolved: boolean) {
    if (resolved) {
      this.service.revokeReviewFlag(+this.csskey, +this.appkey, 1);
    }
  }

  /**
   * Show what changed for an applicability question flagged with a significant update.
   */
  onViewSigChange(ac: AppSect) {
    this.service.showLoadingAnimation(true);
    this.service
      .reviewApplicabilityUpdate(+this.csskey, +this.appkey, ac.appSectKey)
      .subscribe(
        (content) => {
          this.service.showLoadingAnimation(false);
          const dialogConfig = new MatDialogConfig();
          dialogConfig.data = {
            content: content,
            rbName: this.selectedRbName,
            csskey: +this.csskey,
            appkey: +this.appkey,
          } as ApplicabilityReviewDialogData;
          dialogConfig.width = "800px";
          dialogConfig.position = { top: "5vh" };
          const dialogRef = this.dialog.open(
            DisplayApplicabilityReviewDialog,
            dialogConfig
          );

          dialogRef.afterClosed().subscribe((result) => {
            if (result) {
              // Clear the flag locally so the icon disappears immediately. The backend already
              // clears the underlying 'needs review' gate server-side the moment this page's
              // GetApplicableQuestions call loads, so there is no separate flag-removal call to make.
              ac.sigChangeUpdate = false;
              this.service.revokeReviewFlag(
                +this.csskey,
                +this.appkey,
                3,
                ac.appSectKey
              );
            }
          });
        },
        (err) => {
          this.service.showLoadingAnimation(false);
          console.error(err);
          this.alertService.error(err.error);
        }
      );
  }
}

/**
 * Dialog window to display the old/new question diff for a single applicability
 * question that was flagged with a significant change.
 */
@Component({
  selector: "displayApplicabilityReview",
  templateUrl: "applicability-review.html",
  styleUrls: ["./edit-applicability.component.css"],
  standalone: false,
})
export class DisplayApplicabilityReviewDialog implements OnInit {
  newQuestion: string;
  // No old counterpart means this question was just added (a new rulebook, or a new
  // section within an existing one) - there was never a previous answer to drop.
  isNewQuestion: boolean;

  constructor(
    public dialogRef: MatDialogRef<DisplayApplicabilityReviewDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ApplicabilityReviewDialogData
  ) {}

  ngOnInit() {
    this.isNewQuestion = !this.data.content.oldQuestion;
    // TblAppSection.Question already carries an embedded '<b>Question:</b>' label, HTML-
    // encoded as stored - decode it first, then strip the tag, same order and pattern
    // loadApplicability() uses for the question list, since the dialog template supplies
    // its own 'Question:' label.
    const stripLabel = (html: string) =>
      decodeHtml(html ?? "").replace(/<b>Question:<\/b>/gi, "");
    this.newQuestion = diffString(
      stripHtml(stripLabel(this.data.content.oldQuestion)),
      stripHtml(stripLabel(this.data.content.newQuestion))
    );
  }

  onRemoveFlag(): void {
    this.dialogRef.close(true);
  }

  onClose(): void {
    this.dialogRef.close(null);
  }
}
